import React from "react";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import { COLORS } from "./constants";
import { commonInputProps, inputSx } from "./styles";
import { CopyAdornment } from "./CopyAdornment";

// ─────────────────────────────────────────────────────────────────────────────
// SrtUrlBreakdown
//
// The one-line SRT URL is what you paste into a player that takes a full MRL.
// Encoders mostly do not: OBS, vMix and Larix ask for server, port, stream id
// and latency in SEPARATE boxes, and a 300-character URL gives no hint which
// fragment belongs in which box.
//
// So each part is presented the same way the PASSPHRASE row already is — a
// label beside a read-only field with its own copy button — because that row
// is the one thing on this panel already shaped for "type this somewhere else".
//
// This PARSES; it never rebuilds. The URL above remains the source of truth and
// is not modified, reordered or re-encoded here.
// ─────────────────────────────────────────────────────────────────────────────

export function parseSrtUrl(raw) {
  if (!raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }
  if (!u.hostname) return null;

  const q = u.searchParams;
  // searchParams ALREADY percent-decodes. streamid arrives as
  // `#!::r=<id>,m=publish` — exactly the text an encoder expects — so it must
  // NOT be run through decodeURIComponent a second time.
  const streamId = q.get("streamid");
  const keyLen   = q.get("pbkeylen");
  const latency  = q.get("latency");

  return {
    host:       u.hostname,
    port:       u.port || "",
    mode:       q.get("mode") || "",
    streamId:   streamId || "",
    latency:    latency ? `${latency} ms` : "",
    encryption: keyLen ? `AES-${Number(keyLen) * 8}` : ""
  };
}

// Same shape as the PASSPHRASE row: fixed-width caps label, read-only field,
// copy button in the adornment.
function Field({ label, value, fieldId, copiedField, onCopy, labelWidth = 74 }) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Typography
        sx={{
          color: COLORS.muted, fontSize: 10, fontWeight: 600,
          whiteSpace: "nowrap", letterSpacing: "0.05em",
          width: labelWidth, flexShrink: 0
        }}
      >
        {label}
      </Typography>
      <TextField
        {...commonInputProps}
        value={value}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <CopyAdornment value={value} fieldId={fieldId}
              copiedField={copiedField} onCopy={onCopy} />
          )
        }}
        sx={{ ...inputSx, flex: 1, minWidth: 0 }}
      />
    </Stack>
  );
}

export function SrtUrlBreakdown({
  url, idPrefix, copiedField, onCopy, accent,
  passphraseNote = "Passphrase is shown below. It is the same for both URLs and changes every time you start the relay.",
  heading = "Or enter these fields manually", plain = false
}) {
  const parts = parseSrtUrl(url);
  if (!parts) return null;

  return (
    <Box sx={plain ? {} : { mt: 1.25, pt: 1.25, borderTop: `1px dashed ${COLORS.border}` }}>
      <Typography
        sx={{
          color: accent || COLORS.muted, fontSize: 10, fontWeight: 700,
          letterSpacing: "0.06em", textTransform: "uppercase", mb: 1
        }}
      >
        {heading}
      </Typography>

      {/* One field per row: this sits inside a half-width card, and two per
          row left about 35px for the value itself, so it rendered empty. */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Field label="SERVER" value={parts.host}
            fieldId={`${idPrefix}Host`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
        <Grid item xs={12}>
          <Field label="PORT" value={parts.port}
            fieldId={`${idPrefix}Port`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
        <Grid item xs={12}>
          <Field label="MODE" value={parts.mode}
            fieldId={`${idPrefix}Mode`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
        <Grid item xs={12}>
          <Field label="LATENCY" value={parts.latency}
            fieldId={`${idPrefix}Latency`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
        <Grid item xs={12}>
          <Field label="ENCRYPTION" value={parts.encryption}
            fieldId={`${idPrefix}Enc`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
        {/* Full width: the longest value, and the one most often got wrong.
            Inside the URL it is percent-encoded (%23!%3A%3Ar%3D…), so anyone
            reading it off the link by eye pastes the escaped form and the
            handshake is refused. This is the decoded text. */}
        <Grid item xs={12}>
          <Field label="STREAM ID" value={parts.streamId}
            fieldId={`${idPrefix}StreamId`} copiedField={copiedField} onCopy={onCopy} />
        </Grid>
      </Grid>

      {/* Passphrase is deliberately absent: it is identical for both URLs and
          already has its own row below, so repeating it here would put the
          same secret on screen three times. */}
      {passphraseNote && (
        <Typography sx={{ color: COLORS.muted, fontSize: 10, mt: 1 }}>
          {passphraseNote}
        </Typography>
      )}
    </Box>
  );
}
